/**
 * This is the top-level component that defines your UI application.
 *
 * This is an appropriate spot for application wide components and configuration,
 * stuff like application chrome (headers, footers, navigation, etc), routing
 * (what urls go where), etc.
 *
 * @see https://github.com/reactjs/react-router-tutorial/tree/master/lessons
 */

import React, { useState } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { RouteComponentProps } from 'react-router';
import { BrowserRouter, Route, Redirect, Switch } from 'react-router-dom';
import { Header, Content, Footer, Layout, textStyles, LeftSider } from '@allenai/varnish';
import Menu from 'antd/es/menu';
import Tooltip from 'antd/es/tooltip';
import { Link } from '@allenai/varnish-react-router';
import Alert from 'antd/es/alert';

import { AppRoute, AllRoutes, RouteGroups, AppRouteGroup } from './AppRoute';
import { ImgIcon } from './components';
import headerLogo from './logo.svg';

const GlobalStyle = createGlobalStyle`
    .ant-tooltip-inner {
        width: 500px;
    }
`;

export const App = ({ location }: RouteComponentProps) => {
    const [menuCollapsed, setMenuCollapsed] = useState(false);
    const siderWidthExpanded = '225px';
    /**
     * TODO: Figure out why this must be 80px, and no other value, varnish
     * sets this explicitly in CSS, so I'm not sure why the collapsedWidth
     * property is provided.
     */
    const siderWidthCollapsed = '80px';

    const renderMenuItems = (group: AppRouteGroup) =>
        group.routes.map((route) => (
            <Menu.Item key={route.label} disabled={route.disabled}>
                <IconMenuItem route={route} menuCollapsed={menuCollapsed} />
            </Menu.Item>
        ));
    return (
        <BrowserRouter>
            <GlobalStyle />
            <Route path="/">
                <Layout bgcolor="white">
                    <Header>
                        <Header.Columns columns="auto 1fr">
                            <Header.Logo label={<Header.AppName>Multiverse</Header.AppName>}>
                                <Logo />
                            </Header.Logo>
                            {process.env._ENV !== 'prod' && (
                                <Alert
                                    message={`You are currently in an environment that uses the staging database`}
                                    type="warning"
                                    banner
                                    showIcon
                                />
                            )}
                        </Header.Columns>
                    </Header>
                    <Layout>
                        <LeftSider
                            width={siderWidthExpanded}
                            collapsedWidth={siderWidthCollapsed}
                            collapsible
                            collapsed={menuCollapsed}
                            onCollapse={() => setMenuCollapsed(!menuCollapsed)}>
                            <Menu
                                defaultSelectedKeys={[location?.pathname]}
                                // first 5 open
                                defaultOpenKeys={['0', '1', '2', '3', '4']}
                                mode="inline"
                                aria-expanded={!menuCollapsed}>
                                {RouteGroups.map((group, k) =>
                                    group.label ? (
                                        <Menu.ItemGroup
                                            key={k}
                                            title={
                                                !menuCollapsed ? (
                                                    <textStyles.Small>
                                                        {group.label}
                                                    </textStyles.Small>
                                                ) : null
                                            }>
                                            {renderMenuItems(group)}
                                        </Menu.ItemGroup>
                                    ) : (
                                        renderMenuItems(group)
                                    )
                                )}
                            </Menu>
                        </LeftSider>
                        <Layout>
                            <Content main>
                                <Switch>
                                    <Redirect exact from="/" to="/category/papers" />
                                    {AllRoutes.map(({ path, exact, component: Component }) => (
                                        <Route
                                            exact={exact}
                                            key={path}
                                            path={path}
                                            component={Component}
                                        />
                                    ))}
                                </Switch>
                            </Content>
                            <Footer />
                        </Layout>
                    </Layout>
                </Layout>
            </Route>
        </BrowserRouter>
    );
};

const Logo = styled.img.attrs({
    src: headerLogo,
    alt: 'Multiverse Logo',
    height: '54px',
})``;

const IconMenuItemColumns = styled.div`
    display: grid;
    grid-template-columns: min-content 1fr;
    justify-items: start;
    align-items: center;
    grid-gap: 0;
`;

const IconMenuItem = ({ route, menuCollapsed }: { route: AppRoute; menuCollapsed: boolean }) => {
    return (
        <Tooltip title={menuCollapsed ? route.label : null}>
            <Link to={route.path}>
                {!menuCollapsed ? (
                    <IconMenuItemColumns>
                        {route.iconName ? (
                            <ImgIcon
                                name={route.iconName}
                                type={route.iconType}
                                alt={route.label || 'menu icon'}
                            />
                        ) : null}
                        <textStyles.Small>{route.label}</textStyles.Small>
                    </IconMenuItemColumns>
                ) : (
                    <>
                        {route.iconName ? (
                            <ImgIcon
                                name={route.iconName}
                                type={route.iconType}
                                alt={route.label || 'menu icon'}
                            />
                        ) : null}
                    </>
                )}
            </Link>
        </Tooltip>
    );
};
